// BL598 Phase 2 — `gridctl delete grid <slug>`.
//
// Deletes a grid you own, when it is empty. Resolves the slug to its grid_id via
// GET /api/v2/grids, then issues DELETE /api/v2/grids/:grid_id. The server is the
// authority on the owner + empty preconditions and returns the failure reason
// (FORBIDDEN_ROLE, GRID_NOT_EMPTY, etc.); the CLI is a thin client and surfaces
// whatever it says.
//
// NOTE: the backing DELETE /api/v2/grids/:grid_id endpoint is not yet shipped
// server-side (Phase 2 is CLI-only, no API changes). Until it lands this verb
// surfaces the server's not-found/not-allowed response. Tracked as a follow-up
// in docs/backlog/598.
//
// Confirmation by default; `--yes` skips the prompt for scripting.

import { Command } from 'commander';
import inquirer from 'inquirer';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { CloudGridError } from '../../utils.js';

// Decision 045: dual-emit — prefer `grid_id`, fall back to legacy `org_id`.
interface OrgListRow {
  grid_id?: string;
  org_id?: string;
  slug: string;
  name: string;
}

export interface DeleteGridOptions {
  yes?: boolean;
}

export async function runDeleteGrid(
  client: IV2Client,
  slug: string,
  opts: DeleteGridOptions,
): Promise<void> {
  const list = await client.get<{ orgs: OrgListRow[] }>('/api/v2/grids');
  const match = (list.orgs ?? []).find((o) => o.slug === slug);
  if (!match) {
    const available = (list.orgs ?? []).map((o) => o.slug).join(', ') || '(none)';
    throw new CloudGridError(
      `You are not a member of grid '${slug}'. Available: ${available}`,
    );
  }
  const gridId = match.grid_id ?? match.org_id;
  if (!gridId) {
    throw new CloudGridError(`Grid '${slug}' is missing an id in the server response.`);
  }

  if (!opts.yes) {
    const { ok } = await inquirer.prompt([
      {
        type: 'confirm',
        name: 'ok',
        message: `Delete grid ${slug}? This cannot be undone. The grid must be empty and you must own it.`,
        default: false,
      },
    ]);
    if (!ok) {
      process.stdout.write('Cancelled.\n');
      return;
    }
  }

  await client.del(`/api/v2/grids/${encodeURIComponent(gridId)}`);
  process.stdout.write(`Deleted grid ${slug}.\n`);
}

export const deleteGridCommand = new Command('grid')
  .description('Delete a grid you own (must be empty)')
  .argument('<slug>', 'Grid slug')
  .option('--yes', 'Skip the confirmation prompt')
  .action(async (slug: string, opts: DeleteGridOptions) => {
    try {
      const client = await getV2Client();
      await runDeleteGrid(client, slug, opts);
    } catch (err) {
      await handleV2Error(err);
    }
  });
